import React, { useEffect, useState } from 'react';
import {
  EditBase,
  SimpleForm,
  SaveButton,
  TextInput,
  Toolbar,
  useNotify,
  useRedirect,
  required,
  useDataProvider,
  useEditContext,
} from "react-admin";
import { useFormContext } from 'react-hook-form';
import Box from "@mui/material/Box";
import {
  Button,
  Chip,
  Checkbox,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

const FormSyncComponent: React.FC<{ selectedActionIds: number[] }> = ({ selectedActionIds }) => {
  const form = useFormContext();

  useEffect(() => {
    if (form) {
      form.setValue('_actionIdsSync', selectedActionIds.join(','), {
        shouldDirty: true,
      });
    }
  }, [selectedActionIds, form]);

  return null;
};

const RoleFormToolbar = () => (
  <Toolbar
    sx={{
      mt: '10px',
      position: 'sticky',
      bottom: 0,
      zIndex: 2,
      px: 0,
      pt: 2,
      pb: 1,
      bgcolor: '#ffffff',
      borderTop: '1px solid #e2e8f0',
      justifyContent: 'flex-end',
      '& .RaSaveButton-button': {
        minWidth: 120,
        height: 42,
        px: 2.5,
        borderRadius: '12px',
        textTransform: 'none',
        fontSize: '0.95rem',
        fontWeight: 700,
        background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
        boxShadow: '0 10px 20px rgba(79, 70, 229, 0.25)',
        '&:hover': {
          background: 'linear-gradient(135deg, #1d4ed8 0%, #4338ca 100%)',
          boxShadow: '0 12px 24px rgba(79, 70, 229, 0.32)',
        },
      },
    }}
  >
    <SaveButton label="Save Role" />
  </Toolbar>
);

const RoleEditForm: React.FC = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const { record } = useEditContext();

  const [menus, setMenus] = useState<any[]>([]);
  const [actions, setActions] = useState<any[]>([]);
  const [selectedActionIds, setSelectedActionIds] = useState<number[]>([]);
  const [selectedMenuIds, setSelectedMenuIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  // 从 record 中解析已保存的 menuIds（兼容多种后端返回格式）
  const getInitialMenuIds = (r: any): number[] => {
    if (!r) return [];

    if (Array.isArray(r.menuIds)) {
      return r.menuIds
        .map((id: any) => Number(id))
        .filter(Number.isFinite);
    }

    if (Array.isArray(r.menus)) {
      return r.menus
        .map((m: any) =>
          Number(m.menuId ?? m.id)
        )
        .filter(Number.isFinite);
    }

    if (Array.isArray(r.roleMenus)) {
      return r.roleMenus
        .map((rm: any) => Number(rm.menuId))
        .filter(Number.isFinite);
    }

    return [];
  };

  // 从 record 中解析已保存的 actionIds（兼容多种后端返回格式）
  const getInitialActionIds = (r: any): number[] => {
  if (!r) return [];

  if (Array.isArray(r.actionIds)) {
    return r.actionIds
      .map((id: any) => Number(id))
      .filter(Number.isFinite);
  }

  if (Array.isArray(r.actions)) {
    return r.actions
      .map((a: any) =>
        Number(a.actionId ?? a.id)
      )
      .filter(Number.isFinite);
  }

  if (Array.isArray(r.roleActions)) {
    return r.roleActions
      .map((ra: any) => Number(ra.actionId))
      .filter(Number.isFinite);
  }

  return [];
  };
// 加载所有菜单、所有权限、以及当前角色的详细信息（包括已关联的权限），并初始化状态
  useEffect(() => {
    if (!record || !record.id) return;
    setLoading(true);
    Promise.all([
      dataProvider.getList('permissionMenus', { pagination: { page: 1, perPage: 1000 }, sort: { field: 'id', order: 'ASC' }, filter: {} }),
      dataProvider.getList('permissionActions', { pagination: { page: 1, perPage: 2000 }, sort: { field: 'id', order: 'ASC' }, filter: {} }),
      dataProvider.getOne('permissionRoles', { id: record.id }), // 重新获取角色详细信息，包括关联数据
    ])
      .then(([menusRes, actionsRes, roleRes]) => {
        console.log('[RoleEdit] Loaded menus:', menusRes.data);
        console.log('[RoleEdit] Loaded actions:', actionsRes.data);
        console.log('[RoleEdit] Loaded role data:', roleRes.data);
        setMenus(menusRes.data);
        setActions(actionsRes.data);
        setSelectedActionIds(getInitialActionIds(roleRes.data));
        setSelectedMenuIds(getInitialMenuIds(roleRes.data));
        setLoading(false);
      })
      .catch((error) => {
        console.error('[RoleEdit] Error loading role data:', error);
        notify('Error loading menus or actions', { type: 'warning' });
        setLoading(false);
      });
  }, [dataProvider, notify, record]);

  if (loading) return <span>Loading...</span>;
  if (!record) return <span>No role record found</span>;
// 将 actions 按 menuId 分组，方便后续渲染
  const actionsByMenu = actions.reduce((acc: Record<string, any[]>, action: any) => {
    const key = action.menuId ?? 'none';
    acc[key] = acc[key] || [];
    acc[key].push(action);
    return acc;
  }, {});
// 将菜单分为两级，并按 title 字母顺序排序
  const sortByTitle = (a: any, b: any) =>
    String(a.title || '').localeCompare(String(b.title || ''));
  const level1Menus = menus.filter((m: any) => m.parentId == null).sort(sortByTitle);
  const childrenMap = new Map<string, any[]>();
  menus.forEach((m: any) => {
    if (m.parentId == null) return;
    const key = String(m.parentId);
    if (!childrenMap.has(key)) childrenMap.set(key, []);
    childrenMap.get(key)!.push(m);
  });
  childrenMap.forEach((arr) => arr.sort(sortByTitle));

  const toId = (x: any) => Number(x);
  const isSelected = (id: any) => selectedActionIds.includes(toId(id));
// 切换单个 action 的选中状态
  const toggleAction = (id: number) => {
    const n = toId(id);
    setSelectedActionIds(prev =>
      prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n]
    );
  };

  const toggleMenuWithoutActions = (menuId: number) => {
    setSelectedMenuIds(prev =>
      prev.includes(menuId)
        ? prev.filter(x => x !== menuId)
        : [...prev, menuId]
    );
  };

 // 切换菜单时，如果菜单下有 actions，则全选/全不选这些 actions
  const toggleMenu = (menuActions: any[]) => {
    const ids = menuActions.map(a => a.id);
    const allSelected = ids.every(id => selectedActionIds.includes(id));

    setSelectedActionIds(prev =>
      allSelected
        ? prev.filter(id => !ids.includes(id))
        : Array.from(new Set([...prev, ...ids]))
    );
  };

  const allActionIds = actions
    .map((a: any) => Number(a.id))
    .filter(Number.isFinite);

  const menuIdsWithoutActions = menus
    .filter((m: any) => !(actionsByMenu[m.id] || []).length)
    .map((m: any) => m.id);

  const handleSelectAll = () => {
    setSelectedActionIds(allActionIds);
    setSelectedMenuIds(menuIdsWithoutActions);
  };

  const handleClearAll = () => {
    setSelectedActionIds([]);
    setSelectedMenuIds([]);
  };

  const selectedMenuCount = Array.from(
    new Set([
      ...actions
        .filter((a: any) => isSelected(a.id))
        .map((a: any) => a.menuId)
        .filter((id: any) => id != null),
      ...selectedMenuIds,
    ])
  ).length;

  const handleSave = async (data: any) => {
    try {
      // 合并所有 menuIds：1. 从选中的 actions 推导的 menuIds 2. 直接选中的没有 action 的菜单
      const menuIdsFromActions = Array.from(
        new Set(
          actions
            .filter((a: any) => isSelected(a.id))
            .map((a: any) => a.menuId)
            .filter((id: any) => id != null)
        )
      );
      const allMenuIds = Array.from(new Set([...menuIdsFromActions, ...selectedMenuIds]));
      
      console.log('[RoleEdit handleSave]');
      console.log('  selectedActionIds:', selectedActionIds);
      console.log('  selectedMenuIds (menus without actions):', selectedMenuIds);
      console.log('  menuIdsFromActions (derived from selected actions):', menuIdsFromActions);
      console.log('  Final allMenuIds to save:', allMenuIds);
      
      const payload = {
        roleName: data.roleName ?? '',
        description: data.description ?? '',
        menuIds: allMenuIds,
        actionIds: selectedActionIds,
      };

      console.log('[RoleEdit] Saving payload:', payload);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
      const token = localStorage.getItem('token');

      const response = await fetch(`${apiUrl}/permissionRoles/${record.id}`, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Update failed with status ${response.status}`);
      }

      notify('Role updated successfully', { type: 'success' });
      redirect('list', 'permissionRoles');
    } catch (error) {
      console.error('[RoleEdit] Update failed:', error);
      notify('Update failed', { type: 'error' });
    }
  };

  return (
    <SimpleForm
      sx={{
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
        border: '1px solid #e5e7eb',
        borderRadius: '16px',
        p: 3,
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.04)',
        '& > .MuiStack-root': {
          flex: 1,
          overflow: 'visible',
          flexDirection: 'column',
          alignItems: 'stretch',
        },
      }}
      onSubmit={handleSave}
      toolbar={<RoleFormToolbar />}
    >
      <FormSyncComponent selectedActionIds={selectedActionIds} />
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: '14px',
          border: '1px solid #e2e8f0',
          bgcolor: '#ffffff',
          mb: 2,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography variant="subtitle1" fontWeight={700}>
            Basic Information
          </Typography>
          <Chip size="small" label="Edit Role" sx={{ bgcolor: '#ecfeff', color: '#155e75', fontWeight: 600 }} />
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <Box display="flex" flexWrap="wrap" gap={2} sx={{ flexShrink: 0 }}>
          <Box flex="1 1 48%">
            <TextInput source="roleName" label="Role Name" fullWidth validate={[required()]} />
          </Box>
          <Box flex="1 1 48%">
            <TextInput source="description" label="Description" fullWidth />
          </Box>
        </Box>
      </Paper>

      <Box
        flex="1 1 100%"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'visible',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: '14px',
            border: '1px solid #e2e8f0',
            bgcolor: '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle1" fontWeight={700}>
                Menus & Actions
              </Typography>
              <Chip size="small" label={`Actions: ${selectedActionIds.length}`} color="primary" variant="outlined" />
              <Chip size="small" label={`Menus: ${selectedMenuCount}`} color="success" variant="outlined" />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" onClick={handleSelectAll}>Select All</Button>
              <Button variant="text" size="small" color="inherit" onClick={handleClearAll}>Clear</Button>
            </Stack>
          </Stack>
          <Divider sx={{ mb: 1.5 }} />
          <Box
            sx={{
              flex: 1,
              overflow: 'visible',
              py: 0.5,
              pr: 0.5,
              '& .MuiFormControlLabel-root': { mr: 0 },
            }}
          >
          {level1Menus.map((menu1) => {
            const level2Menus = childrenMap.get(String(menu1.id)) || [];
            const actions1 = actionsByMenu[menu1.id] || [];

            const allIds1 = actions1.map((a: any) => a.id);
            const allIds2 = level2Menus.flatMap((m: any) =>
              (actionsByMenu[m.id] || []).map((a: any) => a.id)
            );
            const allIds = [...allIds1, ...allIds2];

            // Check if menu1 has any actions (directly or through children)
            const hasAnyActions = allIds.length > 0;
            const allChecked1 = hasAnyActions
              ? allIds.every((id) => isSelected(id))
              : selectedMenuIds.includes(menu1.id);

            return (
              <Paper key={menu1.id} elevation={0} sx={{ mb: 1.5, border: '1px solid #e5e7eb', borderRadius: '12px', p: 1.25, bgcolor: '#fcfdff' }}>
                {/* 一级菜单 - show all menus regardless of actions */}
                <Box sx={{ py: 0.25, display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={allChecked1}
                        onChange={() => {
                          if (hasAnyActions) {
                            // 有 actions：切换 actions 选中状态
                            toggleMenu(
                              allIds.length ? actions.filter((a: any) => allIds.includes(a.id)) : []
                            );
                          } else {
                            // 没有 actions：直接切换菜单选中状态
                            toggleMenuWithoutActions(menu1.id);
                          }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight="600">
                        {menu1.title}
                      </Typography>
                    }
                  />
                </Box>
                {/* 一级菜单下的 actions（若有） */}
                {actions1.length > 0 && (
                  <Box sx={{ pl: 4, mb: 0.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {actions1.map((action: any) => (
                      <FormControlLabel
                        key={action.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={isSelected(action.id)}
                            onChange={() => toggleAction(action.id)}
                          />
                        }
                        label={<Chip size="small" label={action.actionName} variant="outlined" />}
                      />
                    ))}
                  </Box>
                )}
                {/* 二级菜单及各自 actions - show all level2 menus even without actions */}
                {level2Menus.map((menu2) => {
                  const menu2Actions = actionsByMenu[menu2.id] || [];
                  const hasActions2 = menu2Actions.length > 0;
                  const allChecked2 = hasActions2
                    ? menu2Actions.every((a: any) =>
                        isSelected(a.id)
                      )
                    : selectedMenuIds.includes(menu2.id);

                  return (
                    <Box key={menu2.id} sx={{ pl: 4, mb: 0.75 }}>
                      <Box sx={{ py: 0.25 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={allChecked2}
                              onChange={() => {
                                if (hasActions2) {
                                  // 有 actions：切换 actions 选中状态
                                  toggleMenu(menu2Actions);
                                } else {
                                  // 没有 actions：直接切换菜单选中状态
                                  toggleMenuWithoutActions(menu2.id);
                                }
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" fontWeight="500">
                              {menu2.title}
                            </Typography>
                          }
                        />
                      </Box>
                      {menu2Actions.length > 0 && (
                        <Box sx={{ pl: 4, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                          {menu2Actions.map((action: any) => (
                            <FormControlLabel
                              key={action.id}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={isSelected(action.id)}
                                  onChange={() => toggleAction(action.id)}
                                />
                              }
                              label={<Chip size="small" label={action.actionName} variant="outlined" />}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Paper>
            );
          })}
          </Box>
        </Paper>
      </Box>
    </SimpleForm>
  );
};

const RoleEdit: React.FC = () => (
  <EditBase resource="permissionRoles">
    <RoleEditForm />
  </EditBase>
);

export default RoleEdit;
